import styled, { keyframes } from 'styled-components';
import { getColorScheme } from './theme/colorSchemes';

/* ─── Shared SVG Components ─── */
export const MicrophoneIcon = ({ color = 'currentColor', size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 14C13.66 14 15 12.66 15 11V5C15 3.34 13.66 2 12 2C10.34 2 9 3.34 9 5V11C9 12.66 10.34 14 12 14Z" fill={color}/>
    <path d="M17 11C17 13.76 14.76 16 12 16C9.24 16 7 13.76 7 11H5C5 14.53 7.61 17.43 11 17.92V21H13V17.92C16.39 17.43 19 14.53 19 11H17Z" fill={color}/>
  </svg>
);

/* ─── Animations ─── */
const msgIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ─── Widget Wrapper ─── */
export const WidgetWrapper = styled.div`
  &, & *, & *::before, & *::after { box-sizing: border-box !important; }
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
`;

export const LoadingOverlay = styled.div`
  position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,0.92); z-index: 9999;
`;

export const FadeInWrapper = styled.div`
  opacity: ${p => (p.$loaded ? 1 : 0)};
  transition: opacity 0.6s ease-in-out;
`;

/* ─── Desktop 2-Column Layout ─── */
export const DesktopLayout = styled.div`
  display: flex;
  height: 100vh;
  background: ${p => getColorScheme(p.$scheme).bgPage};
  overflow: hidden;
`;

/* ─── Expert Strip (left) ─── */
export const ExpertStripContainer = styled.div`
  width: 280px; min-width: 280px;
  background: ${p => getColorScheme(p.$scheme).bgStrip};
  border-right: 1px solid ${p => getColorScheme(p.$scheme).divider};
  display: flex; flex-direction: column; overflow: hidden;
`;

export const ExpertCard = styled.div`
  padding: 28px 22px 20px;
  text-align: center;
  position: relative;
  &::after {
    content: ''; position: absolute; bottom: 0; left: 22px; right: 22px; height: 1px;
    background: linear-gradient(90deg, transparent, #e2e5eb, transparent);
  }
`;

export const ExpertImg = styled.img`
  width: 72px; height: 72px; border-radius: 18px; object-fit: cover;
  border: 2px solid #fff; box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  margin-bottom: 12px;
`;

export const ExpertName = styled.div`
  font-size: 16px; font-weight: 700; color: #0f172a; margin-bottom: 2px;
`;

export const ExpertTitle = styled.div`
  font-size: 12px; color: #64748b; margin-bottom: 10px; line-height: 1.4;
`;

export const ExpertRatingRow = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 3px; font-size: 13px;
`;

/* ─── Quick Info Items ─── */
export const QuickInfo = styled.div`
  flex: 1; padding: 16px 18px; overflow-y: auto;
  display: flex; flex-direction: column; gap: 6px;
  &::-webkit-scrollbar { display: none; }
  -ms-overflow-style: none; scrollbar-width: none;
`;

export const InfoItem = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; border-radius: 12px;
  background: #fff; border: 1px solid ${p => getColorScheme(p.$scheme).divider};
  transition: all 0.2s; cursor: ${p => p.onClick ? 'pointer' : 'default'};
  &:hover {
    border-color: ${p => getColorScheme(p.$scheme).accentBorder};
    background: ${p => getColorScheme(p.$scheme).accentSoft};
  }
`;

export const InfoIcon = styled.div`
  width: 32px; height: 32px; border-radius: 8px;
  background: ${p => getColorScheme(p.$scheme).accentSoft};
  color: ${p => getColorScheme(p.$scheme).primary};
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; flex-shrink: 0;
`;

export const InfoLabel = styled.div`
  font-size: 10px; color: #94a3b8; font-weight: 500; text-transform: uppercase; letter-spacing: 0.3px;
`;

export const InfoValue = styled.div`
  font-size: 12.5px; color: #334155; font-weight: 500; line-height: 1.3;
  display: flex; align-items: center; gap: 4px; flex-wrap: wrap;
  /* A URL or an email address is one unbreakable token with no space to wrap at,
     so without these it pushes the row wider than the card and runs off the edge.
     min-width:0 lets the flex item shrink; overflow-wrap lets the token break. */
  min-width: 0;
  overflow-wrap: anywhere;
`;

/* Text column of an InfoItem. min-width:0 is the part that matters: a flex child
   defaults to min-width:auto and therefore refuses to shrink below its content,
   which is what let long values overflow. */
export const InfoText = styled.div`
  flex: 1;
  min-width: 0;
`;

export const OpenBadge = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; font-weight: 600; color: #16a34a;
  &::before { content: ''; width: 5px; height: 5px; border-radius: 50%; background: #22c55e; }
`;

export const ClosedBadge = styled.span`
  font-size: 11px; font-weight: 600; color: #ef4444;
`;

/* ─── Progress Circle Section ─── */
export const ProgressSection = styled.div`
  padding: 16px 18px 20px;
  border-top: 1px solid ${p => getColorScheme(p.$scheme).divider};
  background: linear-gradient(180deg, #fff 0%, #fafbfc 100%);
  display: flex; align-items: center; gap: 14px;
`;

export const RingWrap = styled.div`
  position: relative; width: 64px; height: 64px; flex-shrink: 0;
`;

export const RingText = styled.div`
  position: absolute; inset: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  line-height: 1;
`;

export const RingCurrent = styled.span`
  font-size: 20px; font-weight: 800; color: #0f172a;
`;

export const RingDivider = styled.div`
  width: 18px; height: 1.5px; background: #dde1e7; border-radius: 1px; margin: 3px 0;
`;

export const RingTotal = styled.span`
  font-size: 11px; font-weight: 600; color: #94a3b8;
`;

export const ProgressInfo = styled.div`
  flex: 1; min-width: 0;
`;

export const ProgressPhase = styled.div`
  font-size: 13px; font-weight: 700; color: #0f172a; margin-bottom: 2px;
  display: flex; align-items: center; gap: 6px;
`;

export const PhaseBadge = styled.span`
  font-size: 9px; font-weight: 700; padding: 2px 7px; border-radius: 6px;
  background: ${p => getColorScheme(p.$scheme).accentSoft};
  color: ${p => getColorScheme(p.$scheme).primary};
  text-transform: uppercase; letter-spacing: 0.3px;
`;

export const ProgressHint = styled.div`
  font-size: 11px; color: #94a3b8; line-height: 1.4;
`;

export const SegBar = styled.div`
  display: flex; gap: 3px; margin-top: 8px;
`;

export const Seg = styled.div`
  flex: 1; height: 3px; border-radius: 2px;
  background: ${p => p.$done ? '#22c55e' : p.$active ? getColorScheme(p.$scheme).primary : '#edf0f4'};
  transition: background 0.3s;
`;

/* ─── Chat Area ─── */
export const ChatAreaContainer = styled.div`
  flex: 1; display: flex; flex-direction: column;
  background: #fff; min-width: 0; overflow: hidden;
`;

/* Chat header */
export const ChatTopBar = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 22px; border-bottom: 1px solid #f1f3f7; flex-shrink: 0;
`;

export const BotBadge = styled.div`
  display: flex; align-items: center; gap: 6px;
  padding: 5px 12px 5px 6px; border-radius: 20px;
  background: ${p => getColorScheme(p.$scheme).accentSoft};
  border: 1px solid ${p => getColorScheme(p.$scheme).accentBorder};
`;

export const BotDot = styled.div`
  width: 24px; height: 24px; border-radius: 50%;
  background: ${p => getColorScheme(p.$scheme).primary};
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; color: #fff;
`;

export const BotName = styled.span`
  font-size: 12px; font-weight: 600; color: ${p => getColorScheme(p.$scheme).primaryDark};
`;

export const TopActions = styled.div`
  display: flex; gap: 6px;
`;

export const TopBtn = styled.button`
  width: 32px; height: 32px; border-radius: 10px;
  border: 1px solid #edf0f4; background: #fff; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.2s; color: #94a3b8;
  &:hover {
    background: ${p => getColorScheme(p.$scheme).accentSoft};
    color: ${p => getColorScheme(p.$scheme).primary};
    border-color: ${p => getColorScheme(p.$scheme).accentBorder};
  }
`;

/* Messages container */
export const MessagesContainer = styled.div`
  flex: 1; padding: 20px 22px; overflow-y: auto;
  display: flex; flex-direction: column; gap: 14px;
  scroll-behavior: smooth;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #dde1e7; border-radius: 4px; }
`;

/* System pill */
export const SystemPill = styled.div`
  align-self: center; padding: 5px 14px; border-radius: 20px;
  background: #f8f9fb; border: 1px solid #edf0f4;
  font-size: 11px; font-weight: 500; color: #64748b;
  display: flex; align-items: center; gap: 5px;
`;

/* Message row */
export const MessageRow = styled.div`
  display: flex; gap: 8px; max-width: 80%;
  animation: ${msgIn} 0.35s ease-out;
  align-self: ${p => p.$type === 'user' ? 'flex-end' : 'flex-start'};
  flex-direction: ${p => p.$type === 'user' ? 'row-reverse' : 'row'};
`;

export const MessageAvatar = styled.div`
  width: 28px; height: 28px; border-radius: 10px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; align-self: flex-end;
  background: ${p => p.$type === 'user'
    ? getColorScheme(p.$scheme).accentSoft
    : '#f1f3f7'};
  color: ${p => p.$type === 'user' ? getColorScheme(p.$scheme).primary : '#64748b'};
`;

export const MessageContent = styled.div`
  display: flex; flex-direction: column; gap: 2px;
`;

export const Bubble = styled.div`
  padding: 12px 16px; font-size: 13.5px; line-height: 1.6;
  border-radius: 16px; word-break: break-word;
  ${p => p.$type === 'user' ? `
    background: ${getColorScheme(p.$scheme).primary};
    color: #fff;
    border-bottom-right-radius: 6px;
  ` : `
    background: #f8f9fb;
    border: 1px solid #eef1f5;
    border-bottom-left-radius: 6px;
    color: #334155;
  `}
  strong { font-weight: 600; color: ${p => p.$type === 'user' ? '#fff' : '#0f172a'}; }
  table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 12.5px; }
  th {
    text-align: left; padding: 7px 10px;
    background: ${p => getColorScheme(p.$scheme).accentSoft};
    color: #334155; font-weight: 600;
    border-bottom: 1px solid ${p => getColorScheme(p.$scheme).accentBorder};
  }
  th:first-child { border-radius: 8px 0 0 0; }
  th:last-child { border-radius: 0 8px 0 0; }
  td { padding: 7px 10px; border-bottom: 1px solid #f1f3f7; color: #475569; }
  tr:last-child td { border: none; }
  ul, ol { margin: 6px 0 0 16px; padding: 0; }
  li { margin-bottom: 3px; }
  a { color: ${p => p.$type === 'user' ? '#fff' : getColorScheme(p.$scheme).primary}; text-decoration: underline; font-weight: 500; }
  a:hover { opacity: 0.85; }
`;

export const MsgTime = styled.div`
  font-size: 10px; color: #b0b8c4; padding: 0 4px;
  text-align: ${p => p.$type === 'user' ? 'right' : 'left'};
`;

/* Typing indicator */
const typingBounce = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-5px); opacity: 1; }
`;

export const TypingRow = styled.div`
  display: flex; align-items: center; gap: 8px; align-self: flex-start;
`;

export const TypingBubble = styled.div`
  display: flex; gap: 4px; padding: 14px 18px;
  background: #f8f9fb; border: 1px solid #eef1f5;
  border-radius: 16px; border-bottom-left-radius: 6px;
  span {
    width: 6px; height: 6px; border-radius: 50%; background: #b0b8c4;
    animation: ${typingBounce} 1.3s ease-in-out infinite;
    &:nth-child(2) { animation-delay: 0.15s; }
    &:nth-child(3) { animation-delay: 0.3s; }
  }
`;

/* Quick reply chips */
export const QuickReplies = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; padding: 0 4px; margin-top: 4px;
`;

export const QRChip = styled.button`
  padding: 8px 16px; border-radius: 20px;
  font-size: 12.5px; font-weight: 500; cursor: pointer;
  transition: all 0.2s; font-family: inherit;
  ${p => p.$primary ? `
    background: ${getColorScheme(p.$scheme).primary}; color: #fff; border: none;
    &:hover { background: ${getColorScheme(p.$scheme).primaryDark}; transform: translateY(-1px);
      box-shadow: 0 4px 12px ${getColorScheme(p.$scheme).accentGlow}; }
  ` : `
    background: #fff; color: #334155; border: 1.5px solid #e2e5eb;
    &:hover { border-color: ${getColorScheme(p.$scheme).primary};
      color: ${getColorScheme(p.$scheme).primary};
      background: ${getColorScheme(p.$scheme).accentSoft}; }
  `}
`;

/* ─── Input Area ─── */
export const InputArea = styled.div`
  padding: ${p => p.$isMobile ? '8px 12px 10px' : '12px 22px 18px'};
  border-top: 1px solid #f1f3f7; flex-shrink: 0;
`;

export const InputRow = styled.div`
  display: flex; align-items: flex-end; gap: ${p => p.$isMobile ? '6px' : '8px'};
  padding: ${p => p.$isMobile ? '5px 6px' : '6px 8px'};
  border-radius: ${p => p.$isMobile ? '14px' : '16px'};
  border: 1.5px solid #e8ecf2; background: #fff;
  transition: all 0.25s;
  &:focus-within {
    border-color: ${p => getColorScheme(p.$scheme).primary};
    box-shadow: 0 0 0 3px ${p => getColorScheme(p.$scheme).accentSoft};
  }
`;

export const InBtn = styled.button`
  width: ${p => p.$isMobile ? '32px' : '34px'};
  height: ${p => p.$isMobile ? '32px' : '34px'};
  border-radius: ${p => p.$isMobile ? '9px' : '10px'};
  border: none; background: ${p => p.$send ? getColorScheme(p.$scheme).primary : '#f8f9fb'};
  color: ${p => p.$send ? '#fff' : '#94a3b8'};
  cursor: pointer; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
  &:hover {
    background: ${p => p.$send ? getColorScheme(p.$scheme).primaryDark : getColorScheme(p.$scheme).accentSoft};
    color: ${p => p.$send ? '#fff' : getColorScheme(p.$scheme).primary};
  }
`;

export const InField = styled.textarea`
  flex: 1; border: none; outline: none; font-family: inherit;
  font-size: ${p => p.$isMobile ? '14px' : '13.5px'};
  resize: none; min-height: 22px; max-height: 100px;
  padding: 6px 4px; color: #1e293b; background: transparent;
  line-height: 1.45;
  &::placeholder { color: #b0b8c4; }
`;

export const PoweredRow = styled.div`
  text-align: center; margin-top: ${p => p.$isMobile ? '6px' : '8px'};
  font-size: ${p => p.$isMobile ? '9px' : '10px'}; color: #b0b8c4;
  display: flex; align-items: center; justify-content: center; gap: 3px;
  a { text-decoration: none; display: flex; align-items: center; gap: 3px; color: inherit; }
  strong { color: #94a3b8; }
`;

/* ─── Recording States ─── */
const pulseAnim = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
`;

export const RecordingIconBtn = styled(InBtn)`
  animation: ${pulseAnim} 1.5s ease-in-out infinite;
  background: rgba(255,0,0,0.08);
  &:hover { background: rgba(255,0,0,0.14); }
`;

export const RecordingTimer = styled.div`
  position: absolute; top: -25px; left: 50%; transform: translateX(-50%);
  background: #fff; color: #e53e3e;
  padding: 2px 8px; border-radius: 12px; font-size: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  display: flex; align-items: center; justify-content: center; z-index: 10;
  border: 1px solid ${p => getColorScheme(p.$scheme).accentBorder};
`;

export const RecordingStatus = styled.div`
  position: absolute; top: -25px; left: 50%; transform: translateX(-50%);
  background: #fff; color: #4a5568;
  padding: 2px 8px; border-radius: 12px; font-size: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1); z-index: 10;
  border: 1px solid ${p => getColorScheme(p.$scheme).accentBorder};
`;

/* ─── File Upload Pill ─── */
export const FilePill = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
  padding: 8px 14px; border-radius: 10px;
  background: ${p => getColorScheme(p.$scheme).accentSoft};
  border: 1px solid ${p => getColorScheme(p.$scheme).accentBorder};
  font-size: 12px; font-weight: 500; color: #334155; margin-top: 4px;
`;

/* ─── Popup / Modal ─── */
export const PopupOverlay = styled.div`
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0,0,0,0.25); backdrop-filter: blur(4px);
  display: flex; justify-content: center; align-items: center; z-index: 1000;
`;

export const PopupContent = styled.div`
  background: #fff; color: #2d3748; padding: 24px; border-radius: 16px;
  max-width: 500px; width: 90%; text-align: center;
  border: 1px solid ${p => getColorScheme(p.$scheme).accentBorder};
  box-shadow: 0 8px 32px ${p => getColorScheme(p.$scheme).accentShadow};
`;

export const PopupButton = styled.button`
  background: ${p => getColorScheme(p.$scheme).primary}; color: #fff;
  padding: 12px 24px; border: none; border-radius: 10px;
  margin-top: 20px; cursor: pointer; font-size: 16px; font-weight: 500;
  transition: all 0.2s; font-family: inherit;
  &:hover {
    background: ${p => getColorScheme(p.$scheme).primaryHover};
    transform: translateY(-1px);
  }
`;

/* ─── Iframe Header ─── */
export const IframeHeader = styled.div`
  width: 100%; color: white;
  border-radius: 10px 10px 0 0;
  display: flex; flex-direction: column;
  background: ${p => {
    const c = getColorScheme(p.$scheme);
    return `linear-gradient(90deg, ${c.primary}, ${c.primaryDark})`;
  }};
`;

export const IFrameChatWindow = styled.div`
  background: #fff; border-radius: 8px;
  width: 100%; max-width: 100%; min-width: 0;
  flex: 1; padding: 0; overflow: hidden;
  display: flex; flex-direction: column;
  &::-webkit-scrollbar { display: none; }
`;

export const LogoWrapper = styled.div`
  height: 100%; position: relative; top: var(--logo-vertical-position);
  display: flex; align-items: center;
  font-family: 'Inria Sans', sans-serif; padding-left: 1px;
`;

export const Logo = styled.div`
  background-image: url(${p => p.url});
  background-position: center; background-repeat: no-repeat; background-size: contain;
  width: 70px; height: 75%; padding-right: 5px;
`;

export const ExitButtonWrapper = styled.div`
  height: 25px; display: flex; justify-content: space-between;
  align-items: center; background: none; border: none; cursor: pointer; margin-right: 20px;
`;

/* ─── Misc ─── */
export const createMessagesHash = (messages) => messages.map(msg => msg.message).join('|');
